import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import {
  ProposalRequest,
  ProposalRequestFile,
  ProposalRequestVendor
} from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ProposalRequestService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  retrieveProposalRequestService<type>(
    oneOrMany: 'one' | 'many' | 'count',
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<type>;
        } = {
          Client: () =>
            this.http.get<type>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequest/${oneOrMany}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalRequests(query = {}) {
    return this.retrieveProposalRequestService<ProposalRequest[]>(
      'many',
      query
    );
  }

  getProposalRequest(query = {}) {
    return this.retrieveProposalRequestService<ProposalRequest>('one', query);
  }

  getProposalRequestCount(query = {}) {
    return this.retrieveProposalRequestService<number>('count', query);
  }

  saveProposalRequest(ProposalRequest: ProposalRequest, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequest>;
        } = {
          Client: () =>
            this.http.post<ProposalRequest>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequest`,
              ProposalRequest,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveProposalRequestFile(
    ProposalRequestFile: ProposalRequestFile,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequest>;
        } = {
          Client: () =>
            this.http.post<ProposalRequest>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestFile`,
              ProposalRequestFile,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteProposalRequestFile(
    ProposalRequestFile: ProposalRequestFile,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequest>;
        } = {
          Client: () =>
            this.http.delete<ProposalRequest>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestFile`,
              { body: ProposalRequestFile, params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalRequestVendor(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequestVendor>;
        } = {
          Vendor: () =>
            this.http.get<ProposalRequestVendor>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalRequestVendor/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<ProposalRequestVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestVendor/one`,
              { params: query }
            )
          // 'Admin': () => this.http.get(``, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalRequestVendors(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequestVendor[]>;
        } = {
          Vendor: () =>
            this.http.get<ProposalRequestVendor[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalRequestVendor/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<ProposalRequestVendor[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestVendor/many`,
              { params: query }
            )
          // 'Admin': () => this.http.get(``, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalRequestVendorCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalRequestVendor/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestVendor/count`,
              { params: query }
            )
          // 'Admin': () => this.http.get(``, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveProposalRequestVendor(
    ProposalRequestVendor: ProposalRequestVendor,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<ProposalRequestVendor>;
        } = {
          Vendor: () =>
            this.http.post<ProposalRequestVendor>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalRequestVendor`,
              ProposalRequestVendor,
              { params: query }
            ),
          Client: () =>
            this.http.post<ProposalRequestVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestVendor`,
              ProposalRequestVendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  sendProposalRequestVendorEmail(
    ProposalRequestVendor: ProposalRequestVendor,
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<void>;
        } = {
          Client: () =>
            this.http.post<void>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestVendor/email`,
              ProposalRequestVendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalRequestFileS3PresignedUrl(
    ProposalRequestId: number,
    FileId: number
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalRequestFile/presignedUrl`,
              { params: { ProposalRequestId, FileId } }
            ),
          Vendor: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalRequestFile/presignedUrl`,
              { params: { ProposalRequestId, FileId } }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getProposalFileS3PresignedUrl(ProposalId: number, FileId: number) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/qspr/ProposalFile/presignedUrl`,
              { params: { ProposalId, FileId } }
            ),
          Vendor: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/qspr/ProposalFile/presignedUrl`,
              { params: { ProposalId, FileId } }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
